import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import HandleChange from "../../../../../Helpers/HandleChange";
import {
    TABS_VISITOR,
    VISITOR_MODEL
} from "../../Model/Model";
import {GetUserDetailForUpdate} from "../../../../../reduxToolkit/Contractor/ContractorApi";
import {useLocation, useNavigate} from "react-router-dom";
import {
    getIdentityValidationTypeById,
    IDENTITY_DOCUMENTS_MODEL,
    IDENTITY_VALIDATION_CARD_MODEL,
    TABS_VISITOR_UPDATE
} from "../Model/Model";
import {GetAllUserFiles} from "../../../../../reduxToolkit/UserFile/UserFileApi";
import {STATUS_MODEL} from "../../../../../enums/statusEnum";
import {UpdateUserProfile} from "../../../../../reduxToolkit/Commons/CommonsApi";
import {UpdateUserStatus} from "../../../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import {toast} from "react-toastify";
import routes from "../../../../../Routes/routes";
import {VISITOR_VIEWS_FROM} from '../../Guard/Model/Model';
import {isPhoneFormat} from "../../../../../Apis/functions";

/**
 * @fileoverview React hook-based component for updating visitor information.
 * Fetches user details, manages identity validation status, and allows toggling user state.
 *
 * @module VisitorUpdateApp
 * @author Karen Yunuen Pozos Arias
 * @email kpozos@ibl.mx
 * @jobTitle Full Stack Developer
 *
 * @returns {Object} Contains state and handler functions used by the Visitor Update module.
 */

export const VisitorUpdateApp = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    let { viewOrigin, id: userId , disabled : disabledState } = location?.state;
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);

    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [disabled, setDisabled] = useState(disabledState);
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(true);
    const [finalArray, setFinalArray] = useState([]);
    const [item, setItem] = useState({ ...VISITOR_MODEL });
    const [identityDocuments, setIdentityDocuments] = useState({ ...IDENTITY_DOCUMENTS_MODEL });
    const [identityValidationType, setIdentityValidationType] = useState(null);
    const [identityValidationStatus, setIdentityValidationStatus] = useState(null);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showDeniedModal, setShowDeniedModal] = useState(false);
    const [showActiveInactiveModal, setShowActiveInactiveModal] = useState(false);
    const [modified, setModified] = useState(false);
    const [statusId, setStatusId] = useState(STATUS_MODEL.PRE_REGISTERED);

    /**
     * Contains modal configurations for identity validation actions.
     */
    const identityValidationModals = [
        {
            show: showApproveModal,
            setter: setShowApproveModal,
            title: 'approve_id_validation',
            description: 'you_can_leave_a_optional_comment',
            status: STATUS_MODEL.VALIDATED,
            required: false,
            successTrans:"has_been_validated_successfully"
        },
        {
            show: showDeniedModal,
            setter: setShowDeniedModal,
            title: 'decline_id_validation',
            description: 'please_provide_a_reason',
            status: STATUS_MODEL.DENIED,
            required: true,
            successTrans:"has_been_denied_successfully"
        },
    ];

    /**
     * Initializes the steps with translated labels.
     */
    useEffect(() => {
        setSteps([
            t(TABS_VISITOR_UPDATE.OVERVIEW.TRANS),
            t(TABS_VISITOR_UPDATE.IDENTITY_VERIFICATION.TRANS),
            t(TABS_VISITOR_UPDATE.INVITATION_HISTORY.TRANS),
            t(TABS_VISITOR_UPDATE.REQUEST_ACCESS.TRANS),
        ]);
    }, [t]);

    /**
     * Refetches user data if a modification occurred.
     */
    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Fetches visitor and file data on initial load.
     */
    useEffect(() => {
        getData();
    }, []);

    /**
     * Fetches visitor data and their uploaded files.
     * Sets the internal state accordingly.
     */
    const getData = () => {
        dispatch(GetUserDetailForUpdate(userId)).then(({ payload: { data: { data } } }) => {
            let aux = data;
            if (aux?.id) {
                setItem(VISITOR_MODEL.set(aux));
                setStatusId(aux?.status?.id);
                setIdentityValidationType(getIdentityValidationTypeById(aux?.identityValidationType));
                setIdentityValidationStatus(
                    IDENTITY_VALIDATION_CARD_MODEL.getIdentityValidationStatus(aux?.identityValidation?.id)
                );
                dispatch(GetAllUserFiles(userId)).then(({ payload: { data: { data } } }) => {
                    setIdentityDocuments(IDENTITY_DOCUMENTS_MODEL.set(data, aux));
                });
            }
        });
    };

    /**
     * Changes the active step in the UI.
     * @param {number} step - The index of the step to activate.
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    /**
     * Submits the updated visitor profile to the backend.
     */
    const handleUpdate = () => {
        if(!item.isValid() || !validatePhone(item.phoneNumber)){
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);
        dispatch(UpdateUserProfile({data:item.toRegister()}));
    };

    /**
     * Toggles visitor status between ACTIVE and PRE_REGISTERED.
     * Shows a toast notification based on the new status.
     */
    const handleUpdateStatus = () => {
        let newStatus = statusId === STATUS_MODEL.ACTIVE ? STATUS_MODEL.INACTIVE : STATUS_MODEL.ACTIVE;
        dispatch(UpdateUserStatus({ id: item.id, statusId: newStatus })).then(({ payload: { data: { success } } }) => {
            if (success) {
                setStatusId(newStatus);
                if (newStatus === STATUS_MODEL.ACTIVE) toast.success(t('has_been_activate_successfully'));
                else toast.success(t('has_been_deactivate_successfully'));
                setModified(true);
            }
        });
    };

    /**
     * Navigates back to the visitor list view.
     */
    const handleOnBack = () => {
        const urlBack = viewOrigin == VISITOR_VIEWS_FROM.VISITOR_MANAGEMENT ? routes.visitor.list : routes.visitor.listGuard;
        navigate(urlBack, {
            state: { currentTab: TABS_VISITOR.VISITORS.INDEX },
        });
    };

    /**
     * Validates whether the given phone number matches the expected format.
     *
     * @param phone the phone number to validate
     * @return {@code true} if the phone number matches the required format;
     *         {@code false} otherwise
     */
    const validatePhone = (phone) => {
        const isValid = isPhoneFormat(phone);
        setIsValidPhoneNumber(isValid);
        return isValid;
    };

    return {
        t,
        DEV,
        permission,
        location,
        steps,
        activeStep,
        setActiveStep,
        userId,
        item,
        disabled,
        handleChange,
        emptyFields,
        isValidPhoneNumber,
        finalArray,
        setFinalArray,
        identityDocuments,
        identityValidationType,
        identityValidationStatus,
        identityValidationModals,
        modified,
        setModified,
        showApproveModal,
        setShowApproveModal,
        showDeniedModal,
        setShowDeniedModal,
        showActiveInactiveModal,
        setShowActiveInactiveModal,
        statusId,
        handleUpdateStatus,
        handleStepChange,
        handleUpdate,
        handleOnBack,
        validatePhone
    };
};
